import { useState, useEffect, useCallback, useMemo } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { User } from '../../../../types';
import { useNotificationService } from '../../../Notification';
import { GET_USERS_PAGINATED, COUNT_USERS, CREATE_USER, UPDATE_USER, DELETE_USER, DISABLE_USER, ENABLE_USER } from '../../../../graphql/queries';
import { useAdminTranslation, useCommonTranslation } from '../../../../modules/i18n';

const PAGE_SIZE = 6;
const DEBOUNCE_MS = 300;

export interface UseUsersReturn {
  // State
  users: User[];
  loading: boolean;
  showUserModal: boolean;
  editingUser: User | null;
  selectedUser: User | null;
  showUserDetailModal: boolean;

  // Pagination
  currentPage: number;
  totalPages: number;
  totalCount: number;
  pageSize: number;
  showingFrom: number;
  showingTo: number;
  handlePageChange: (page: number) => void;

  // Filters
  searchQuery: string;
  handleSearchChange: (query: string) => void;

  // Actions
  handleCreateUser: () => void;
  handleEditUser: (user: User) => void;
  handleDeleteUser: (userId: string) => void;
  handleDisableUser: (userId: string) => void;
  handleEnableUser: (userId: string) => void;
  handleSaveUser: (userData: Partial<User>) => void;
  handleViewUserDetail: (user: User) => void;

  // Modal controls
  setShowUserModal: (show: boolean) => void;
  setShowUserDetailModal: (show: boolean) => void;
}

interface ConfirmDialogState {
  isOpen: boolean;
  title: string;
  message: string;
  onConfirm: () => void;
}

// Build Prisma where clause from search query
function buildWhereClause(search: string): object | undefined {
  if (!search) return undefined;
  return {
    OR: [{ username: { contains: search, mode: 'insensitive' } }, { email: { contains: search, mode: 'insensitive' } }]
  };
}

export const useUsers = (confirmDialog: ConfirmDialogState, setConfirmDialog: (dialog: ConfirmDialogState) => void): UseUsersReturn => {
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showUserDetailModal, setShowUserDetailModal] = useState(false);

  // Pagination + filter state
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [totalCount, setTotalCount] = useState(0);

  const { showSuccess } = useNotificationService();
  const { t } = useAdminTranslation();
  const { t: tCommon } = useCommonTranslation();

  // Debounce search input & reset page together to avoid race condition
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setCurrentPage(1);
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Build where clause
  const where = useMemo(() => buildWhereClause(debouncedSearch), [debouncedSearch]);

  const skip = (currentPage - 1) * PAGE_SIZE;

  // GraphQL hooks
  const {
    data: usersData,
    loading,
    refetch
  } = useQuery(GET_USERS_PAGINATED, {
    variables: { skip, take: PAGE_SIZE, where },
    fetchPolicy: 'network-only'
  });
  const [countUsersMutation] = useMutation(COUNT_USERS);
  const [createUserMutation] = useMutation(CREATE_USER);
  const [updateUserMutation] = useMutation(UPDATE_USER);
  const [deleteUserMutation] = useMutation(DELETE_USER);
  const [disableUserMutation] = useMutation(DISABLE_USER);
  const [enableUserMutation] = useMutation(ENABLE_USER);

  // Fetch count when filters change
  const fetchCount = useCallback(async () => {
    try {
      const args: Record<string, unknown> = {};
      if (where) args.where = where;
      const { data } = await countUsersMutation({ variables: { args } });
      if (data?.countUsers != null) {
        setTotalCount(data.countUsers);
      }
    } catch (err) {
      console.error('Error fetching user count:', err);
    }
  }, [countUsersMutation, where]);

  useEffect(() => {
    fetchCount();
  }, [fetchCount]);

  // Transform GraphQL data
  const users: User[] = useMemo(() => {
    if (!usersData?.findManyUsers) return [];
    return usersData.findManyUsers.map((user: any) => ({
      id: user.id,
      email: user.email,
      username: user.username,
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      role: user.role,
      status: user.status,
      createdAt: new Date(user.createdAt).toLocaleDateString('vi-VN'),
      updatedAt: user.updatedAt ? new Date(user.updatedAt).toLocaleDateString('vi-VN') : undefined
    }));
  }, [usersData]);

  // Pagination computed values
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);
  const showingFrom = totalCount === 0 ? 0 : skip + 1;
  const showingTo = Math.min(skip + PAGE_SIZE, totalCount);

  // Handlers
  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handleSearchChange = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  const refreshData = useCallback(async () => {
    await Promise.all([refetch(), fetchCount()]);
  }, [refetch, fetchCount]);

  const handleCreateUser = () => {
    setEditingUser(null);
    setShowUserModal(true);
  };

  const handleEditUser = (user: User) => {
    setEditingUser(user);
    setShowUserModal(true);
  };

  const handleDeleteUser = (userId: string) => {
    const user = users.find((u) => u.id === userId);
    setConfirmDialog({
      isOpen: true,
      title: t('content.users.messages.deleteTitle'),
      message: t('content.users.messages.deleteMessage', { username: user?.username }),
      onConfirm: async () => {
        try {
          await deleteUserMutation({ variables: { id: userId } });
          showSuccess(tCommon('status.success'), t('content.users.messages.deleteSuccess'));
          setConfirmDialog({ ...confirmDialog, isOpen: false });
          await refreshData();
        } catch (error) {
          console.error('Error deleting user:', error);
          showSuccess(tCommon('status.error'), t('content.users.messages.deleteError'));
        }
      }
    });
  };

  const handleDisableUser = (userId: string) => {
    const targetUser = users.find((u) => u.id === userId);
    setConfirmDialog({
      isOpen: true,
      title: t('content.users.messages.disableTitle'),
      message: t('content.users.messages.disableMessage', {
        username: targetUser?.username,
        defaultValue: `Bạn có chắc muốn vô hiệu hóa ${targetUser?.username}? Người dùng sẽ không thể đăng nhập.`
      }),
      onConfirm: async () => {
        try {
          await disableUserMutation({ variables: { userId } });
          showSuccess(tCommon('status.success'), t('content.users.messages.disableSuccess'));
          setConfirmDialog({ ...confirmDialog, isOpen: false });
          await refreshData();
        } catch (error) {
          console.error('Error disabling user:', error);
          showSuccess(tCommon('status.error'), t('content.users.messages.disableError'));
        }
      }
    });
  };

  const handleEnableUser = (userId: string) => {
    const targetUser = users.find((u) => u.id === userId);
    setConfirmDialog({
      isOpen: true,
      title: t('content.users.messages.enableTitle'),
      message: t('content.users.messages.enableMessage', {
        username: targetUser?.username,
        defaultValue: `Kích hoạt lại ${targetUser?.username}? Người dùng có thể đăng nhập lại.`
      }),
      onConfirm: async () => {
        try {
          await enableUserMutation({ variables: { userId } });
          showSuccess(tCommon('status.success'), t('content.users.messages.enableSuccess'));
          setConfirmDialog({ ...confirmDialog, isOpen: false });
          await refreshData();
        } catch (error) {
          console.error('Error enabling user:', error);
          showSuccess(tCommon('status.error'), t('content.users.messages.enableError'));
        }
      }
    });
  };

  const handleSaveUser = async (userData: Partial<User>) => {
    try {
      if (editingUser) {
        const { data } = await updateUserMutation({
          variables: {
            id: editingUser.id,
            data: {
              email: userData.email,
              firstName: userData.firstName,
              lastName: userData.lastName,
              role: userData.role
            }
          }
        });

        if (data?.updateUser) {
          showSuccess(tCommon('status.success'), t('content.users.messages.updateSuccess'));
        }
      } else {
        const { data } = await createUserMutation({
          variables: {
            email: userData.email!,
            username: userData.username!,
            firstName: userData.firstName,
            lastName: userData.lastName,
            role: userData.role!
          }
        });

        if (data?.createUser) {
          showSuccess(tCommon('status.success'), t('content.users.messages.createSuccess'));
        }
      }

      setShowUserModal(false);
      setEditingUser(null);
      await refreshData();
    } catch (error) {
      console.error('Error saving user:', error);
      showSuccess(tCommon('status.error'), t('content.users.messages.saveError'));
    }
  };

  const handleViewUserDetail = (user: User) => {
    setSelectedUser(user);
    setShowUserDetailModal(true);
  };

  return {
    users,
    loading,
    showUserModal,
    editingUser,
    selectedUser,
    showUserDetailModal,

    currentPage,
    totalPages,
    totalCount,
    pageSize: PAGE_SIZE,
    showingFrom,
    showingTo,
    handlePageChange,

    searchQuery,
    handleSearchChange,

    handleCreateUser,
    handleEditUser,
    handleDeleteUser,
    handleDisableUser,
    handleEnableUser,
    handleSaveUser,
    handleViewUserDetail,

    setShowUserModal,
    setShowUserDetailModal
  };
};
